import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  TwoFactorDisableInput,
  TwoFactorEnableInput,
  TwoFactorEnableResponse,
  TwoFactorRegenerateInput,
  TwoFactorRegenerateResponse,
  TwoFactorSetupResponse,
  TwoFactorStatus,
} from '@news-cms/shared';
import { api } from './api';

async function statusApi(): Promise<TwoFactorStatus> {
  return api<TwoFactorStatus>('/api/admin/auth/2fa/status');
}

export const twoFactorStatusQuery = queryOptions({
  queryKey: ['2fa', 'status'] as const,
  queryFn: statusApi,
  staleTime: 60_000,
});

async function setupApi(): Promise<TwoFactorSetupResponse> {
  return api<TwoFactorSetupResponse>('/api/admin/auth/2fa/setup', { method: 'POST' });
}

async function enableApi(input: TwoFactorEnableInput): Promise<TwoFactorEnableResponse> {
  return api<TwoFactorEnableResponse>('/api/admin/auth/2fa/enable', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

async function disableApi(input: TwoFactorDisableInput): Promise<{ ok: true }> {
  return api<{ ok: true }>('/api/admin/auth/2fa/disable', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

async function regenerateApi(
  input: TwoFactorRegenerateInput,
): Promise<TwoFactorRegenerateResponse> {
  return api<TwoFactorRegenerateResponse>('/api/admin/auth/2fa/regenerate-codes', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function useTwoFactorSetup() {
  return useMutation({ mutationFn: setupApi });
}

export function useTwoFactorEnable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: enableApi,
    onSuccess: () => qc.invalidateQueries({ queryKey: twoFactorStatusQuery.queryKey }),
  });
}

export function useTwoFactorDisable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: disableApi,
    onSuccess: () => qc.invalidateQueries({ queryKey: twoFactorStatusQuery.queryKey }),
  });
}

export function useTwoFactorRegenerate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: regenerateApi,
    onSuccess: () => qc.invalidateQueries({ queryKey: twoFactorStatusQuery.queryKey }),
  });
}
